// Reset initialization flag when itemId changes

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { FormProvider as RHFFormProvider } from "react-hook-form";

// Default options
const defaultOptions = {
  isNewItemCheck: (context) => {
    const { itemId } = context;
    return itemId === "+" || !itemId;
  },
  isArchivedItemCheck: (response) => {
    return response.isArchived === true;
  },
  errorAdapter: (error) => {
    return {
      name: error.name || "Unknown Error",
      message: error.message || "There was an unknown error. Please try again.",
      fieldErrors: error.errors || {},
    };
  },
};

// Create Form Context
const FormContext = createContext(null);

// Hook to access form context
export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};

/**
 * Main Form component - enhanced with comprehensive state management
 */
export const Form = ({
  form, // The form instance passed from outside
  schema, // Zod schema for validation
  itemId,
  create,
  read,
  update,
  delete: deleteItem,
  archive,
  unarchive,
  onSuccess,
  options = {},
  children,
  className,
}) => {
  const mergedOptions = useMemo(
    () => ({ ...defaultOptions, ...options }),
    [options]
  );

  // Form states
  const [isReading, setIsReading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [error, setError] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    setInitialized(false);
  }, [itemId]);

  // Check if we're creating a new item or editing existing
  const isNewItem = useMemo(() => {
    return mergedOptions.isNewItemCheck({ itemId });
  }, [itemId, mergedOptions]);

  // Loading state combining all operations
  const isLoading = useMemo(() => {
    return (
      isReading ||
      isCreating ||
      isUpdating ||
      isDeleting ||
      isArchiving ||
      isUnarchiving
    );
  }, [
    isReading,
    isCreating,
    isUpdating,
    isDeleting,
    isArchiving,
    isUnarchiving,
  ]);

  // Get values from the form
  const getValues = useCallback(() => form.getValues(), [form]);

  // Dirty values (only the changed fields)
  const dirtyValues = useMemo(() => {
    const values = getValues();

    const dirtyFieldNames = Object.keys(form.formState.dirtyFields || {});

    return dirtyFieldNames.reduce((acc, key) => {
      acc[key] = values[key];
      return acc;
    }, {});
  }, [form.formState.dirtyFields, form.watch()]);

  // Helper to set form error
  const setFormError = useCallback(
    (err) => {
      if (!err) {
        setError(null);
        return;
      }
      setError(mergedOptions.errorAdapter(err));
    },
    [mergedOptions]
  );

  // Response handler for CRUD operations
  const onItemResponse = useCallback(
    (data) => {
      if (!data) return {};

      const values = data?.values || data;

      if (!isNewItem) {
        setIsArchived(mergedOptions.isArchivedItemCheck(data));
      }

      if (values && Object.keys(values).length > 0) {
        // Reset form with new values without triggering rerender loops
        setInitialValues(values);
        form.reset(values, { keepDirtyValues: false });
      }

      return data;
    },
    [isNewItem, mergedOptions, form]
  );

  // Error handler for CRUD operations
  const onItemError = useCallback(
    (err) => {
      // Log JS Errors
      if (err.name === "TypeError") {
        console.error(err);
      }
      setFormError(err);
      throw err; // Rethrow to allow handling in components
    },
    [setFormError]
  );

  // CRUD operations
  const readItem = useCallback(() => {
    if (!read) return Promise.resolve();
    if (isReading) return Promise.resolve(); // Prevent multiple concurrent reads

    setIsReading(true);
    setFormError();

    const context = {
      isNewItem,
      schema,
      values: getValues(),
      dirtyValues: dirtyValues,
      error,
      itemId,
    };

    return read(itemId, context)
      .then((response) => {
        if (response) {
          onItemResponse(response);
        }
        return response;
      })
      .catch(onItemError)
      .finally(() => {
        setIsReading(false);
      });
  }, [
    read,
    itemId,
    isNewItem,
    isReading,
    schema,
    getValues,
    dirtyValues,
    error,
    onItemResponse,
    onItemError,
    setFormError,
  ]);

  const createItem = useCallback(
    (formData) => {
      if (!create) return Promise.resolve();

      setIsCreating(true);
      setFormError();

      const context = {
        isNewItem: true,
        schema,
        values: formData || getValues(),
        dirtyValues: dirtyValues,
        error,
        itemId,
      };

      return create(context)
        .then(onItemResponse)
        .catch(onItemError)
        .finally(() => {
          setIsCreating(false);
        });
    },
    [
      create,
      schema,
      getValues,
      dirtyValues,
      error,
      itemId,
      onItemResponse,
      onItemError,
      setFormError,
    ]
  );

  const updateItem = useCallback(
    (formData) => {
      if (!update) return Promise.resolve();

      setIsUpdating(true);
      setFormError();

      const context = {
        isNewItem: false,
        schema,
        values: formData || getValues(),
        dirtyValues: dirtyValues,
        error,
        itemId,
      };

      return update(itemId, context)
        .then(onItemResponse)
        .catch(onItemError)
        .finally(() => {
          setIsUpdating(false);
        });
    },
    [
      update,
      itemId,
      schema,
      getValues,
      dirtyValues,
      error,
      onItemResponse,
      onItemError,
      setFormError,
    ]
  );

  const deleteItemHandler = useCallback(() => {
    if (!deleteItem) return Promise.resolve();

    setIsDeleting(true);
    setFormError();

    const context = {
      isNewItem: false,
      schema,
      values: getValues(),
      dirtyValues: dirtyValues,
      error,
      itemId,
    };

    return deleteItem(itemId, context)
      .then(onItemResponse)
      .catch(onItemError)
      .finally(() => {
        setIsDeleting(false);
      });
  }, [
    deleteItem,
    itemId,
    schema,
    getValues,
    dirtyValues,
    error,
    onItemResponse,
    onItemError,
    setFormError,
  ]);

  const archiveItemHandler = useCallback(() => {
    if (!archive) return Promise.resolve();

    setIsArchiving(true);
    setFormError();

    const context = {
      isNewItem: false,
      schema,
      values: getValues(),
      dirtyValues: dirtyValues,
      error,
      itemId,
    };

    return archive(itemId, context)
      .then(onItemResponse)
      .catch(onItemError)
      .finally(() => {
        setIsArchiving(false);
      });
  }, [
    archive,
    itemId,
    schema,
    getValues,
    dirtyValues,
    error,
    onItemResponse,
    onItemError,
    setFormError,
  ]);

  const unarchiveItemHandler = useCallback(() => {
    if (!unarchive) return Promise.resolve();

    setIsUnarchiving(true);
    setFormError();

    const context = {
      isNewItem: false,
      schema,
      values: getValues(),
      dirtyValues: dirtyValues,
      error,
      itemId,
    };

    return unarchive(itemId, context)
      .then(onItemResponse)
      .catch(onItemError)
      .finally(() => {
        setIsUnarchiving(false);
      });
  }, [
    unarchive,
    itemId,
    schema,
    getValues,
    dirtyValues,
    error,
    onItemResponse,
    onItemError,
    setFormError,
  ]);

  // Handle form submission based on whether it's a new item or not
  const onSubmit = useCallback(
    (formData) => {
      return isNewItem ? createItem(formData) : updateItem(formData);
    },
    [isNewItem, createItem, updateItem]
  );

  // Initialize form data on mount and when itemId changes
  useEffect(() => {
    // Prevent initialization cycles
    if (initialized) return;

    if (!isNewItem && read) {
      readItem().then(() => {
        setInitialized(true);
      });
    } else {
      // For new items, only set initial values once
      setInitialized(true);
    }
  }, [isNewItem, itemId, initialized]);

  // Create context value
  const formState = {
    // Form state
    form,
    control: form.control,
    values: getValues(),
    dirtyValues,
    errors: form.formState.errors,
    fieldErrors: error?.fieldErrors || {},
    formError: error,
    isNewItem,
    isArchived,
    touched: form.formState.touchedFields,
    dirty: form.formState.dirtyFields,
    isDirty: form.formState.isDirty,
    initialValues,
    schema,

    // Loading states
    isReading,
    isCreating,
    isUpdating,
    isDeleting,
    isArchiving,
    isUnarchiving,
    isLoading,

    // Operations
    onSubmit: form.handleSubmit(onSubmit),
    createItem: () => {
      return new Promise((resolve, reject) => {
        form.handleSubmit(
          (fields) => {
            resolve(createItem(fields));
          },
          (errors) => {
            reject(errors);
          }
        )();
      });
    },
    updateItem: () => {
      return new Promise((resolve, reject) => {
        form.handleSubmit(
          (fields) => {
            resolve(updateItem(fields));
          },
          (errors) => {
            console.error(errors);
            reject(errors);
          }
        )();
      });
    },
    readItem,
    deleteItem: deleteItemHandler,
    archiveItem: archiveItemHandler,
    unarchiveItem: unarchiveItemHandler,

    // Form helpers
    reset: form.reset,
    setValue: form.setValue,
    getValues,
    setError: form.setError,
    clearErrors: form.clearErrors,
    setFormError,
  };

  return (
    <FormProvider form={form} {...formState}>
      <div className={className}>
        {typeof children === "function" ? children(formState) : children}
      </div>
    </FormProvider>
  );
};

// Wrap the form in a RHFFormProvider to provide the form context
const FormProvider = ({ form, children, ...props }) => {
  return (
    <FormContext.Provider value={{ form, ...props }}>
      <RHFFormProvider {...form}>{children}</RHFFormProvider>
    </FormContext.Provider>
  );
};

// Field-level hook - enhanced to work with form control
export const useField = (name) => {
  const { form, fieldErrors = {} } = useFormContext();

  const error = form.formState.errors[name] || fieldErrors[name];
  const value = form.getValues()[name];

  return {
    name,
    value,
    error,
    updateValue: (newValue) =>
      form.setValue(name, newValue, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      }),
    control: form.control,
    register: form.register,
  };
};
